import React, { useState } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { usePermission } from '../../hooks/usePermission';
import { fmtDate } from '../settings/components/membership/utils';
import Navbar from '../home/components/Navbar';
import AvatarUpload from './components/AvatarUpload';
import ProfileDisplay from './components/ProfileDisplay';
import BasicInfoForm from './components/BasicInfoForm';
import SecurityForm from './components/SecurityForm';
import LoginDevicesPanel from './components/LoginDevicesPanel';
import AccountCancellationCard from './components/AccountCancellationCard';

type Tab = 'info' | 'security';

const tabs: { key: Tab; label: string; icon: string }[] = [
  { key: 'info', label: '个人信息', icon: 'ri-user-3-line' },
  { key: 'security', label: '账号安全', icon: 'ri-shield-keyhole-line' },
];

export default function ProfilePage() {
  const { profile, user, refreshProfile } = useAuthStore();
  const [activeTab, setActiveTab] = useState<Tab>('info');
  const [editing, setEditing] = useState(false);

  // 切换标签时退出编辑模式
  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    setEditing(false);
  };

  // 没有 profile 但有 user（party 表无记录）
  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <main className="max-w-[1400px] mx-auto px-6 py-16 flex flex-col items-center justify-center gap-4 text-gray-400">
          <i className="ri-user-unfollow-line text-5xl" />
          <p className="text-lg font-medium text-gray-600">个人资料未找到</p>
          <p className="text-sm">当前账号（{user?.email}）尚未建立个人资料，请联系管理员</p>
        </main>
      </div>
    );
  }

  const displayName = profile.fullName || profile.username || '用户';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <main className="max-w-[1400px] mx-auto px-3 md:px-6 py-4 md:py-8 pb-24 md:pb-8">
        {/* Header */}
        <div className="mb-4 md:mb-6">
          <h1 className="text-xl md:text-2xl font-semibold text-gray-900">个人中心</h1>
          <p className="text-sm text-gray-500 mt-0.5">管理你的个人信息与账号安全设置</p>
        </div>

        {/* ── Mobile: compact profile banner + horizontal tab bar ── */}
        <div className="md:hidden mb-3">
          {/* Profile banner */}
          <div className="bg-white rounded-xl border border-gray-100 p-3 flex items-center gap-3">
            <AvatarUpload
              currentAvatar={profile.avatar}
              displayName={displayName}
              onUploadSuccess={refreshProfile}
            />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-gray-900 truncate text-sm">{displayName}</p>
              <p className="text-xs text-gray-400 truncate">{profile.email ?? ''}</p>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <LevelBadge />
                {profile.createdAt && (
                  <span className="text-xs text-gray-400">
                    {new Date(profile.createdAt).toLocaleDateString('zh-CN')} 加入
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Horizontal tab bar */}
          <div className="flex gap-1.5 mt-3">
            {tabs.map(t => (
              <button
                key={t.key}
                onClick={() => handleTabChange(t.key)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === t.key
                    ? 'bg-teal-500 text-white'
                    : 'bg-white text-gray-600 border border-gray-200'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${t.icon} text-base`} />
                </div>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-start">
          {/* Sidebar – desktop only */}
          <div className="hidden md:flex w-60 shrink-0 flex-col gap-4">
            {/* Avatar + Name */}
            <div className="bg-white rounded-xl border border-gray-100 p-6 flex flex-col items-center">
              <AvatarUpload
                currentAvatar={profile.avatar}
                displayName={displayName}
                onUploadSuccess={refreshProfile}
              />
              <div className="mt-4 text-center w-full">
                <p className="font-semibold text-gray-900 truncate">{displayName}</p>
                <p className="text-xs text-gray-400 mt-0.5 truncate">{profile.email ?? ''}</p>
                <div className="mt-2 flex items-center justify-center gap-2 flex-wrap">
                  <LevelBadge />
                </div>
              </div>
            </div>

            {/* Account meta */}
            <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">账号信息</p>
              <InfoRow icon="ri-calendar-line" label="注册时间">
                {profile.createdAt
                  ? new Date(profile.createdAt).toLocaleDateString('zh-CN')
                  : '未知'}
              </InfoRow>
              {profile.phone && <InfoRow icon="ri-phone-line" label="手机号">{profile.phone}</InfoRow>}
              {profile.companyName && <InfoRow icon="ri-building-line" label="公司">{profile.companyName}</InfoRow>}
              {profile.location && <InfoRow icon="ri-map-pin-line" label="所在地">{profile.location}</InfoRow>}
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-xl border border-gray-100 p-2 space-y-1">
              {tabs.map(t => (
                <button
                  key={t.key}
                  onClick={() => handleTabChange(t.key)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    activeTab === t.key
                      ? 'bg-teal-50 text-teal-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${t.icon} text-base`} />
                  </div>
                  {t.label}
                  {activeTab === t.key && (
                    <span className="ml-auto w-1.5 h-1.5 rounded-full bg-teal-500 inline-block" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 bg-white rounded-xl border border-gray-100 p-4 md:p-8 min-h-[360px] md:min-h-[500px]">
            {activeTab === 'info' && !editing && (
              <ProfileDisplay profile={profile} onEdit={() => setEditing(true)} />
            )}
            {activeTab === 'info' && editing && (
              <>
                <div className="flex items-center justify-between mb-5 md:mb-6">
                  <div>
                    <h2 className="text-base md:text-lg font-semibold text-gray-900">编辑个人信息</h2>
                    <p className="text-xs md:text-sm text-gray-500 mt-0.5">修改基本资料和联系方式</p>
                  </div>
                  <button
                    onClick={() => setEditing(false)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-go-back-line text-sm" />
                    返回
                  </button>
                </div>
                <BasicInfoForm profile={profile} onSaved={() => { refreshProfile(); setEditing(false); }} />
              </>
            )}
            {activeTab === 'security' && (
              <>
                <div className="mb-5 md:mb-6">
                  <h2 className="text-base md:text-lg font-semibold text-gray-900">账号安全</h2>
                  <p className="text-xs md:text-sm text-gray-500 mt-0.5">修改密码、管理登录设备以保护账号安全</p>
                </div>
                <SecurityForm />
                <div className="mt-8 pt-6 border-t border-gray-100">
                  <LoginDevicesPanel />
                </div>
                <div className="mt-8 pt-6 border-t border-gray-100">
                  <AccountCancellationCard />
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

/**
 * 身份徽标。以前这一格写的是 `isAdmin ? '管理员' : '普通用户'`——那是角色不是等级，
 * 尊享会员在个人中心会看到「普通用户」，与「普通会员」这个等级名撞车，看起来像会员掉了。
 * 等级名取服务端下发（后台可改名），未加载完成前不给任何兜底名。
 *
 * 管理员只出角色：`usePermission().can()` 里 isAdmin 直接放行全部功能，会员等级对管理员
 * 不产生任何权限差异，再挂一枚「尊享会员」只会让人以为高级功能来自付费。
 */
function LevelBadge() {
  const { levelName, isPremium, isExpired, expireAt, isAdmin } = usePermission();
  const chip = 'inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full';
  if (isAdmin) {
    return <span className={`${chip} bg-indigo-50 text-indigo-600`}><i className="ri-shield-star-line text-xs" />管理员</span>;
  }
  return (
    <>
      <span className={`${chip} ${isPremium ? 'bg-amber-50 text-amber-600' : 'bg-teal-50 text-teal-600'}`}>
        <i className={`${isPremium ? 'ri-vip-crown-line' : 'ri-user-line'} text-xs`} />
        {levelName || '加载中…'}
      </span>
      {expireAt && (isPremium || isExpired) && (
        <span className="text-xs text-gray-400">
          {isExpired ? `已于 ${fmtDate(expireAt)} 到期` : `有效期至 ${fmtDate(expireAt)}`}
        </span>
      )}
    </>
  );
}

function InfoRow({
  icon,
  label,
  children,
}: {
  icon: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="w-4 h-4 flex items-center justify-center shrink-0">
        <i className={`${icon} text-gray-400 text-sm`} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-400">{label}</p>
        <p className="text-xs font-medium text-gray-700 truncate">{children}</p>
      </div>
    </div>
  );
}
